import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import { ethers } from "ethers";
import { getHandleRegistryReadProvider } from "../../utils/base-read-provider";
import { proveHandleOwnership } from "../../utils/handle-proof";
import { publishHandleRecord } from "../../utils/handle-publication";
import {
  completeHandleRegistration,
  loadHandleRegistration,
  saveHandleRegistration,
  HandleRegistrationPending,
  findLocalHandleRegistrations,
  findPendingHandleNames,
  type PendingHandleRegistration,
} from "../../utils/handle-registration-recovery";
import { lookupHandleAssignment } from "../../utils/handle-assignment";
import { formatInboxPublicKeyHex } from "../../utils/inbox-crypto";
import { formatMpkHex, hashMpkHex } from "../../utils/format";
import { nextHandleEpoch } from "../../utils/handle-epoch";
import {
  computeHandleRecordHashV2,
  handleRecordEntries,
  isHandleRecordV2,
  loadVerifiedHandleRecord,
  normalizeHandle,
  normalizeHandleRecord,
  serializeHandleRecordV2ForSigning,
  sortHandleEntries,
  type HandleEntry,
  type HandleRecordV2,
  type HandleStateReader,
} from "../../utils/handle-resolver";
import {
  HANDLE_HOME_NETWORK,
  HANDLE_PAGE_NETWORK,
  HANDLE_REGISTRY_CHAIN_ID,
  handleNetworkLabel,
  handleRegistryAddress,
  handlesSupportedHere,
} from "../../utils/handle-networks";
import {
  deriveNetworkHandleSecret,
  normalizeHandleWallet,
  type HandleWalletSigner,
  fetchOwnedHandles,
  handleOwnerLookupKey,
  requestHandleOwnershipRoot,
  unwrapHandleSecret,
  wrapHandleSecret,
} from "../../utils/handle-ownership";
import {
  useHandleCryptoHelpers,
  useHandleRegistrationFormState,
  type ClaimedHandleDetails,
} from "../../utils/handle-registration-hooks";
import type { BaseProxyAuthRequestOptions } from "./useBaseProxyAuth";

interface UseStealthHandleRegistrationParams {
  signer?: HandleWalletSigner | null;
  network?: string;
  resolveSigner?: () => Promise<HandleWalletSigner | null>;
  spendingKeys: { mpk: bigint; spendingKey: Uint8Array } | null;
  proxyAuthReady?: boolean;
  derivationSignatureB64Ref: MutableRefObject<string>;
  ensureProxyAuthToken: (
    options?: BaseProxyAuthRequestOptions,
  ) => Promise<string>;
  addLog: (
    message: string,
    type?: "info" | "success" | "error" | "warning",
    txHash?: string,
  ) => void;
}

const ACTION_REGISTER = 1;
const ACTION_ROTATE = 2;
const RECORD_LIFETIME_SECONDS = 90 * 24 * 60 * 60;
const REGISTRY_ABI = [
  "function getHandleState(bytes32) view returns (tuple(bytes32 recordCommitment,bytes32 ownerCommitment,uint64 epoch,bool exists))",
];
const REGISTRY_IFACE = new ethers.Interface([
  "function registerAnonymous(bytes32,bytes32,bytes32,uint256[8],bytes32,uint64)",
  "function rotateAnonymous(bytes32,bytes32,bytes32,uint256[8],bytes32,uint64)",
]);

// The original registration saved a random owner secret, encrypted with the
// home network's key. Only the home page reads it, and never rewrites it with
// a different secret: another network can't decrypt it and must not clobber it.
const legacySecretKey = (handle: string, wallet: string) =>
  `stealthos:handle:hsk:${handle}:${wallet}`;
const pendingPrefix = (registry: string, wallet: string, network: string) =>
  `stealthos:handle:pending:v2:${HANDLE_REGISTRY_CHAIN_ID}:${registry}:${network}:${wallet}:`;
const ownerSecretKey = (
  registry: string,
  network: string,
  wallet: string,
  handle: string,
) =>
  `stealthos:handle:owner:v2:${HANDLE_REGISTRY_CHAIN_ID}:${registry}:${network}:${wallet}:${handle}`;
const legacyPendingPrefix = (registry: string, wallet: string) =>
  `stealthos:handle:pending:v1:${HANDLE_REGISTRY_CHAIN_ID}:${registry}:${wallet}:`;
/** A hint, shared by every network page of this origin, of the wallet's handle. */
const ownedHintKey = (wallet: string) =>
  `stealthos:handle:owned:${normalizeHandleWallet(wallet)}`;
/** Set once a handle's legacy secret is backed up for the other networks. */
const sharedFlagKey = (handle: string, wallet: string) =>
  `stealthos:handle:shared:${handle}:${normalizeHandleWallet(wallet)}`;

function readOwnedHint(wallet: string): string | null {
  try {
    const value = localStorage.getItem(ownedHintKey(wallet)) || "";
    return normalizeHandle(value) === value ? value : null;
  } catch {
    return null;
  }
}

function writeOwnedHint(wallet: string, handle: string) {
  try {
    localStorage.setItem(ownedHintKey(wallet), handle);
  } catch {
    /* a hint only */
  }
}

function stringToHex(str: string): string {
  const encoder = new TextEncoder();
  const bytes = encoder.encode(str);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function serializeForRelayer(tx: {
  to: string;
  data: string;
  value?: string;
}): string {
  const json = JSON.stringify({
    to: tx.to,
    data: tx.data,
    value: tx.value || "0x0",
  });
  return `0x${stringToHex(json)}`;
}

function buildHandleIdempotencyKey(
  action: string,
  handleId: string,
  nullifier: string,
): string {
  const digest = ethers.id(
    ["stealthos-handle-relay-v2", action, handleId, nullifier].join(":"),
  );
  return `handle-v2-${action}-${digest.slice(2)}`;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
}

function bytesFromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** This network's key for its saved attempts (and the legacy secret at home). */
async function deriveHandleStorageKey(
  derivationSignatureB64: string,
  handle: string,
  signerAddress: string,
): Promise<CryptoKey> {
  const signatureMaterial = derivationSignatureB64.trim();
  if (!signatureMaterial) {
    throw new Error(
      "Privacy-key signature is not available; reconnect the wallet once before claiming a handle",
    );
  }
  const domain = ethers.toUtf8Bytes(
    `StealthOS handle local secret storage v2\nhandle=${handle}\nwallet=${normalizeHandleWallet(signerAddress)}`,
  );
  const keyBytes = ethers.getBytes(
    ethers.keccak256(
      ethers.concat([bytesFromBase64(signatureMaterial), domain]),
    ),
  );
  return crypto.subtle.importKey(
    "raw",
    toArrayBuffer(keyBytes),
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"],
  );
}

async function readEncryptedHandleSecret(
  storageKey: string,
  key: CryptoKey,
): Promise<string> {
  const raw = window.localStorage.getItem(storageKey) || "";
  if (/^0x[0-9a-fA-F]{64}$/.test(raw)) {
    // A plaintext secret from an older build: encrypt it in place.
    await writeEncryptedHandleSecret(storageKey, key, raw);
    return raw;
  }
  if (!raw) return "";
  try {
    const parsed = JSON.parse(raw) as { iv?: string; ciphertext?: string };
    if (!parsed.iv || !parsed.ciphertext) return "";
    const plaintext = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: toArrayBuffer(ethers.getBytes(parsed.iv)) },
      key,
      toArrayBuffer(ethers.getBytes(parsed.ciphertext)),
    );
    const decoded = new TextDecoder().decode(plaintext);
    return /^0x[0-9a-fA-F]{64}$/.test(decoded) ? decoded : "";
  } catch {
    return "";
  }
}

async function writeEncryptedHandleSecret(
  storageKey: string,
  key: CryptoKey,
  secret: string,
): Promise<void> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: toArrayBuffer(iv) },
    key,
    toArrayBuffer(new TextEncoder().encode(secret)),
  );
  window.localStorage.setItem(
    storageKey,
    JSON.stringify({
      version: 1,
      iv: ethers.hexlify(iv),
      ciphertext: ethers.hexlify(new Uint8Array(ciphertext)),
    }),
  );
}

/** The record's resolver signature key, derived from the owner secret. The
 * registry commitment is what payers trust; this key needs no storage. */
function resolverSignerFor(hsk: string): ethers.Wallet {
  return new ethers.Wallet(
    ethers.keccak256(
      ethers.concat([
        ethers.toUtf8Bytes("stealthos-handle-resolver-v1"),
        ethers.getBytes(hsk),
      ]),
    ),
  );
}

/** The MPK this network's entry of a saved attempt publishes. */
function attemptMpk(
  publication: Record<string, unknown>,
  network: string,
): string {
  if (isHandleRecordV2(publication))
    return (
      publication.entries.find((entry) => entry.network === network)?.mpk ?? ""
    );
  return String(publication.mpk ?? "");
}

type HandleUpdateMode = "interactive" | "background";

export function useStealthHandleRegistration({
  signer,
  network = HANDLE_PAGE_NETWORK,
  resolveSigner,
  spendingKeys,
  proxyAuthReady = false,
  derivationSignatureB64Ref,
  ensureProxyAuthToken,
  addLog,
}: UseStealthHandleRegistrationParams) {
  const {
    handleInput,
    setHandleInput,
    handleStatus,
    setHandleStatus,
    claimedHandle,
    setClaimedHandle,
    currentOwnerKeysRef,
  } = useHandleRegistrationFormState(spendingKeys);
  const registryAddress = handleRegistryAddress();
  const HOME_PAGE = network === HANDLE_HOME_NETWORK;
  const PAGE_LABEL = handleNetworkLabel(network);
  const supported = handlesSupportedHere(network);
  const { ensurePoseidon, toFieldHex, bytes32ToBigInt } =
    useHandleCryptoHelpers();
  const registeringRef = useRef(false);
  const [activity, setActivity] = useState<
    "register" | "update" | "find" | "recover" | null
  >(null);
  const lookupVersionRef = useRef(0);
  const recoveryKeysRef = useRef<typeof spendingKeys>(null);
  const rootRef = useRef<{ wallet: string; root: string } | null>(null);
  const linkedOwnerRef = useRef<{
    handle: string;
    secret: string;
    keys: typeof spendingKeys;
  } | null>(null);
  const [registrationRevision, setRegistrationRevision] = useState(0);
  const [claimedDetails, setClaimedDetails] =
    useState<ClaimedHandleDetails | null>(null);
  const [suggestedHandle, setSuggestedHandle] = useState<string | null>(null);
  const [shareNeeded, setShareNeeded] = useState(false);

  // The ownership root belongs to one wallet session.
  useEffect(() => {
    rootRef.current = null;
    linkedOwnerRef.current = null;
    setSuggestedHandle(null);
  }, [spendingKeys]);

  const readRegistryState = useCallback<
    (handleId: string) => Promise<{
      recordCommitment: string;
      ownerCommitment: string;
      epoch: number;
      exists: boolean;
    }>
  >(
    async (handleId) => {
      const state = await new ethers.Contract(
        registryAddress,
        REGISTRY_ABI,
        getHandleRegistryReadProvider(),
      ).getHandleState(handleId);
      const epoch = BigInt(state?.epoch ?? 0);
      if (epoch < 0n || epoch > BigInt(Number.MAX_SAFE_INTEGER))
        throw new Error("Invalid on-chain handle epoch");
      return {
        recordCommitment: String(state?.recordCommitment || "").toLowerCase(),
        ownerCommitment: String(state?.ownerCommitment || "").toLowerCase(),
        epoch: Number(epoch),
        exists: Boolean(state?.exists),
      };
    },
    [registryAddress],
  );
  const recordStateReader: HandleStateReader = readRegistryState;

  const handleIdFor = useCallback(
    async (handle: string) => {
      const poseidon = await ensurePoseidon();
      const labelHash = ethers.keccak256(ethers.toUtf8Bytes(handle));
      const hash = poseidon([
        bytes32ToBigInt(labelHash),
        BigInt(HANDLE_REGISTRY_CHAIN_ID),
      ]);
      return {
        labelHash,
        handleId: toFieldHex(BigInt(poseidon.F.toObject(hash).toString())),
      };
    },
    [ensurePoseidon, toFieldHex, bytes32ToBigInt],
  );

  const ownerCommitmentFor = useCallback(
    async (hsk: string, handleId: string) => {
      const poseidon = await ensurePoseidon();
      const hash = poseidon([
        bytes32ToBigInt(hsk),
        bytes32ToBigInt(handleId),
        BigInt(HANDLE_REGISTRY_CHAIN_ID),
      ]);
      return toFieldHex(BigInt(poseidon.F.toObject(hash).toString()));
    },
    [ensurePoseidon, toFieldHex, bytes32ToBigInt],
  );

  const ensureOwnershipRoot = useCallback(
    async (activeSigner: HandleWalletSigner, wallet: string) => {
      if (rootRef.current?.wallet === wallet) return rootRef.current.root;
      setHandleStatus("Confirm the handle signature in your wallet…");
      const root = await requestHandleOwnershipRoot(activeSigner);
      rootRef.current = { wallet, root };
      return root;
    },
    [],
  );

  const checkHandleAvailability = useCallback(async () => {
    if (claimedHandle) return;
    const handle = normalizeHandle(handleInput);
    if (!handle) {
      setHandleStatus("Enter a valid handle, e.g. bob or bob.stealth");
      return;
    }
    if (!supported) {
      setHandleStatus("Handles are unavailable on this network.");
      return;
    }
    try {
      const state = await readRegistryState(
        (await handleIdFor(handle)).handleId,
      );
      setHandleStatus(
        state.exists
          ? `${handle} is registered. If it's yours, use Find my handle or Find with another wallet to link it.`
          : `${handle} is available on every network.`,
      );
    } catch (e) {
      setHandleStatus(
        `Handle check failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  }, [
    handleInput,
    claimedHandle,
    supported,
    readRegistryState,
    handleIdFor,
    PAGE_LABEL,
  ]);

  const refreshClaimedHandle = useCallback(async () => {
    const lookupVersion = ++lookupVersionRef.current;
    const requestSpendingKeys = spendingKeys;
    if (!requestSpendingKeys || !supported) {
      setClaimedHandle(null);
      setHandleInput("");
      return null;
    }
    const recipientKey = await hashMpkHex(
      formatMpkHex(requestSpendingKeys.mpk),
    );
    const handle = await lookupHandleAssignment(recipientKey);
    if (
      currentOwnerKeysRef.current !== requestSpendingKeys ||
      lookupVersion !== lookupVersionRef.current
    ) {
      throw new Error("Account changed. Try again with the current account.");
    }
    setClaimedHandle(handle);
    if (handle) {
      setHandleInput(handle);
    }
    return handle;
  }, [spendingKeys, supported]);

  useEffect(() => {
    if (proxyAuthReady && signer) return;
    void refreshClaimedHandle().catch((error) => {
      if (currentOwnerKeysRef.current === spendingKeys)
        console.warn("[handle] account handle lookup failed:", error);
    });
  }, [refreshClaimedHandle, proxyAuthReady, signer, spendingKeys]);

  // Which networks the account's handle receives on, and when it lapses.
  useEffect(() => {
    if (!claimedHandle || !supported) {
      setClaimedDetails(null);
      return;
    }
    let cancelled = false;
    void loadVerifiedHandleRecord(claimedHandle, {
      readHandleState: recordStateReader,
    })
      .then((record) => {
        if (cancelled || !record) return;
        setClaimedDetails({
          handle: claimedHandle,
          networks: handleRecordEntries(record).map((entry) => entry.network),
          expiresAt: record.expiresAt,
        });
      })
      .catch(() => {
        if (!cancelled) setClaimedDetails(null);
      });
    return () => {
      cancelled = true;
    };
  }, [claimedHandle, supported, registrationRevision]);

  // Remember the handle for this wallet's other network pages, and whether
  // this page holds its secret (registered here, or a legacy saved one) and
  // hasn't shared it with the other networks yet.
  useEffect(() => {
    setShareNeeded(false);
    if (!claimedHandle || !signer || !supported) return;
    let cancelled = false;
    void (async () => {
      const wallet = normalizeHandleWallet(await signer.getAddress());
      if (cancelled) return;
      writeOwnedHint(wallet, claimedHandle);
      if (localStorage.getItem(sharedFlagKey(claimedHandle, wallet))) return;
      if (
        localStorage.getItem(
          ownerSecretKey(registryAddress, network, wallet, claimedHandle),
        ) ||
        (HOME_PAGE &&
          localStorage.getItem(legacySecretKey(claimedHandle, wallet)))
      ) {
        setShareNeeded(true);
        return;
      }
      const signature = derivationSignatureB64Ref.current;
      if (!signature) return;
      const { handleId } = await handleIdFor(claimedHandle);
      const state = await readRegistryState(handleId);
      const owner = await ownerCommitmentFor(
        deriveNetworkHandleSecret(signature, claimedHandle),
        handleId,
      );
      if (!cancelled && state.exists && owner === state.ownerCommitment)
        setShareNeeded(true);
    })().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [
    claimedHandle,
    signer,
    supported,
    registrationRevision,
    network,
    HOME_PAGE,
    registryAddress,
  ]);

  /**
   * Registers a new name, adds this network to a name the wallet already
   * owns, or renews/upgrades the account's own handle: all one registry
   * transaction plus a publication. Background mode only finishes work this
   * browser already proved, and never asks for a signature.
   */
  const finalizeAndPublishHandle = useCallback(
    async (
      handle: string,
      mode: HandleUpdateMode = "interactive",
      options: { link?: boolean; share?: boolean } = {},
    ): Promise<boolean> => {
      const background = mode === "background";
      try {
        const activeSigner = signer || (await resolveSigner?.());
        if (!activeSigner || !spendingKeys) {
          setHandleStatus(
            "Connect your wallet and derive spending keys first.",
          );
          return false;
        }
        if (!supported) {
          setHandleStatus("Handles are unavailable on this network.");
          return false;
        }
        const assertCurrentAccount = () => {
          if (currentOwnerKeysRef.current !== spendingKeys)
            throw new Error(
              "Account changed. Try again with the current account.",
            );
        };

        const signerAddress = normalizeHandleWallet(
          await activeSigner.getAddress(),
        );
        const chainId = HANDLE_REGISTRY_CHAIN_ID;
        const poseidon = await ensurePoseidon();
        const storageKey = await deriveHandleStorageKey(
          derivationSignatureB64Ref.current,
          handle,
          signerAddress,
        );
        const { labelHash, handleId } = await handleIdFor(handle);
        const ownMpk = formatMpkHex(spendingKeys.mpk);

        // A saved attempt resumes exactly as it was proven.
        const pendingKey = `${pendingPrefix(registryAddress, signerAddress, network)}${handle}`;
        let savedUnder = pendingKey;
        let pending = await loadHandleRegistration(pendingKey, storageKey);
        if (!pending && HOME_PAGE) {
          const legacyKey = `${legacyPendingPrefix(registryAddress, signerAddress)}${handle}`;
          pending = await loadHandleRegistration(legacyKey, storageKey);
          if (pending) savedUnder = legacyKey;
        }

        if (!pending) {
          const storedSecretKey = ownerSecretKey(
            registryAddress,
            network,
            signerAddress,
            handle,
          );
          const cached = await readEncryptedHandleSecret(
            storedSecretKey,
            storageKey,
          );
          const legacy =
            cached ||
            (HOME_PAGE
              ? await readEncryptedHandleSecret(
                  legacySecretKey(handle, signerAddress),
                  storageKey,
                )
              : "");
          // Background work resumes only a saved attempt (above) or a claim
          // this browser's legacy secret holds.
          if (background && !legacy) return false;
          // Registering and renewing need no wallet prompt: this network's
          // secret comes from the sign-in privacy-key signature.
          const networkSecret = deriveNetworkHandleSecret(
            derivationSignatureB64Ref.current,
            handle,
          );
          const state = await readRegistryState(handleId);
          assertCurrentAccount();
          const exists = state.exists;
          const owns = async (secret: string) =>
            exists &&
            (await ownerCommitmentFor(secret, handleId)) ===
              state.ownerCommitment;

          const linked =
            options.link &&
            linkedOwnerRef.current?.handle === handle &&
            linkedOwnerRef.current.keys === spendingKeys
              ? linkedOwnerRef.current.secret
              : "";
          let hsk = !exists
            ? networkSecret
            : (await owns(networkSecret))
              ? networkSecret
              : legacy && (await owns(legacy))
                ? legacy
                : linked && (await owns(linked))
                  ? linked
                  : "";
          // Background work only finishes what this browser already holds,
          // and never registers a new name.
          if (background && (!exists || !hsk)) return false;
          let root: string | null = null;
          let backup: string | null = null;
          if (!hsk) {
            if (!options.link && claimedHandle !== handle) {
              throw new Error(
                `${handle} is already taken. If it's yours from another network, choose Find my handle.`,
              );
            }
            // A backup another network's page made for this wallet. The
            // signature stays in this browser; it only opens the backup.
            root = await ensureOwnershipRoot(activeSigner, signerAddress);
            assertCurrentAccount();
            setHandleStatus("Checking your handle…");
            const token = await ensureProxyAuthToken({ interactive: false });
            const owned = await fetchOwnedHandles(
              token,
              handleOwnerLookupKey(root),
            );
            for (const item of owned) {
              if (item.handle !== handle || !item.secret) continue;
              const opened = await unwrapHandleSecret(
                root,
                handle,
                item.secret,
              );
              if (opened && (await owns(opened))) {
                hsk = opened;
                break;
              }
            }
            if (!hsk) {
              throw new Error(
                readOwnedHint(signerAddress) === handle
                  ? `Open StealthOS where you registered ${handle} and choose "Use on all networks" first.`
                  : `${handle} isn't set up for other networks yet. Open StealthOS where you registered it and choose "Use on all networks".`,
              );
            }
          } else if (options.share || (options.link && linked)) {
            // Seal a copy of this network's secret for this wallet's other
            // network pages: one signature, kept in this browser.
            root = await ensureOwnershipRoot(activeSigner, signerAddress);
            assertCurrentAccount();
          }
          if (root) backup = await wrapHandleSecret(root, handle, hsk);
          assertCurrentAccount();
          // Preserve a linked owner's credential under this account's local key.
          // A reconnect or renewal must not require the original network wallet.
          await writeEncryptedHandleSecret(storedSecretKey, storageKey, hsk);
          assertCurrentAccount();

          // The next record keeps every network's entry and sets this one's.
          const current = exists
            ? await loadVerifiedHandleRecord(handle, {
                readHandleState: recordStateReader,
              })
            : null;
          assertCurrentAccount();
          const currentEntries = current ? handleRecordEntries(current) : [];
          const ownEntry: HandleEntry = {
            network: network,
            recipientKey: `0x${await hashMpkHex(ownMpk)}`,
            mpk: ownMpk,
            inboxPublicKey: formatInboxPublicKeyHex(spendingKeys.spendingKey),
          };
          const existingOwn = currentEntries.find(
            (entry) => entry.network === network,
          );
          if (
            existingOwn &&
            existingOwn.recipientKey.toLowerCase() !== ownEntry.recipientKey
          )
            throw new Error(
              `${handle} receives on ${PAGE_LABEL} with a different account. Switch to that account to update it.`,
            );
          const entries = sortHandleEntries([
            ...currentEntries.filter((entry) => entry.network !== network),
            ownEntry,
          ]);

          const epoch = nextHandleEpoch(exists, state.epoch);
          const action = exists ? "rotate" : "register";
          const actionTag = exists ? ACTION_ROTATE : ACTION_REGISTER;
          const record = normalizeHandleRecord({
            format: 2,
            handle,
            chainScope: "any",
            salt: ethers.hexlify(ethers.randomBytes(32)),
            version: epoch,
            expiresAt: Math.floor(Date.now() / 1000) + RECORD_LIFETIME_SECONDS,
            nonce: ethers.hexlify(ethers.randomBytes(32)),
            envId: ethers.keccak256(ethers.toUtf8Bytes(String(chainId))),
            entries,
          }) as HandleRecordV2;
          const recordHash = computeHandleRecordHashV2(record);
          const recordCommitment = toFieldHex(
            BigInt(
              poseidon.F.toObject(
                poseidon([
                  bytes32ToBigInt(recordHash),
                  BigInt(epoch),
                  BigInt(chainId),
                ]),
              ).toString(),
            ),
          );
          const ownerCommitment = await ownerCommitmentFor(hsk, handleId);
          const nullifier = toFieldHex(
            BigInt(
              poseidon.F.toObject(
                poseidon([
                  bytes32ToBigInt(hsk),
                  bytes32ToBigInt(handleId),
                  BigInt(epoch),
                  BigInt(actionTag),
                  BigInt(chainId),
                ]),
              ).toString(),
            ),
          );

          setHandleStatus("Preparing transaction…");
          // Proved in the browser: hsk is the handle's ownership secret, so a
          // prover service that received it could rotate the record and repoint
          // the handle at its own keys.
          const proofFields = await proveHandleOwnership(action, {
            handleId,
            recordCommitment,
            ownerCommitment,
            nullifier,
            handleLabelHash: labelHash,
            recordHash,
            hsk,
            epoch,
            chainId,
          });
          const proof = proofFields.map((v) => BigInt(v)) as unknown as [
            bigint,
            bigint,
            bigint,
            bigint,
            bigint,
            bigint,
            bigint,
            bigint,
          ];
          assertCurrentAccount();

          const calldata = REGISTRY_IFACE.encodeFunctionData(
            exists ? "rotateAnonymous" : "registerAnonymous",
            [
              handleId,
              recordCommitment,
              ownerCommitment,
              proof,
              nullifier,
              epoch,
            ],
          );
          const resolverSigner = resolverSignerFor(hsk);
          const resolverSigningKey = resolverSigner.address.toLowerCase();
          const signature = await resolverSigner.signMessage(
            serializeHandleRecordV2ForSigning(record),
          );
          const publication: Record<string, unknown> = {
            ...record,
            recordHash,
            signature,
            network: network,
            resolverSigningKey,
            // Lets this wallet find the handle from its other network pages.
            ...(root
              ? {
                  ownerLookup: {
                    key: handleOwnerLookupKey(root),
                    secret: backup,
                  },
                }
              : {}),
            onchain: {
              exists: true,
              recordVersion: epoch,
              recordHash,
              resolverSigningKey,
              handleId,
              recordCommitment,
              ownerCommitment,
              nullifier,
              epoch,
              chainId,
              anonRegistry: registryAddress,
            },
          };
          pending = {
            idempotencyKey: buildHandleIdempotencyKey(
              action,
              handleId,
              nullifier,
            ),
            body: {
              handle,
              action,
              chainScope: "any",
              chain_id: chainId,
              handleId,
              recordCommitment,
              ownerCommitment,
              nullifier,
              epoch,
              serialized_transaction: serializeForRelayer({
                to: registryAddress,
                data: calldata,
                value: "0x0",
              }),
              user_address: signerAddress,
            },
            publication,
          };
          await saveHandleRegistration(pendingKey, storageKey, pending);
          savedUnder = pendingKey;
        }

        const attempt: PendingHandleRegistration = pending;
        if (
          attempt.body.handleId !== handleId ||
          attempt.body.user_address !== signerAddress ||
          attempt.body.chain_id !== chainId ||
          attemptMpk(attempt.publication, network) !== ownMpk
        )
          throw new Error(
            "The saved registration belongs to another account. Reconnect the original wallet.",
          );
        setHandleInput(handle);
        setHandleStatus("Finishing your handle setup…");
        const token = await ensureProxyAuthToken({ interactive: false });
        await completeHandleRegistration({
          pending: attempt,
          readState: async () => {
            assertCurrentAccount();
            return readRegistryState(handleId);
          },
          onConfirming: () => setHandleStatus("Confirming registration…"),
          submit: async () => {
            assertCurrentAccount();
            setHandleStatus("Submitting transaction…");
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), 25000);
            try {
              const response = await fetch("/api/handles/relay/submit", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                  "Idempotency-Key": attempt.idempotencyKey,
                },
                body: JSON.stringify(attempt.body),
                signal: controller.signal,
              });
              const data = await response.json().catch(() => ({}));
              if (response.status === 422)
                throw new Error(
                  "Registration was not accepted. Please contact support before trying again.",
                );
              if (response.status === 401)
                throw new Error(
                  "Please sign in again to finish your registration.",
                );
              if (
                !response.ok ||
                data.success === false ||
                !data.transaction_hash
              )
                throw new HandleRegistrationPending();
            } catch (error) {
              if (error instanceof Error && error.name === "AbortError")
                throw new HandleRegistrationPending();
              throw error;
            } finally {
              clearTimeout(timer);
            }
          },
          publish: async () => {
            assertCurrentAccount();
            setHandleStatus("Finishing registration…");
            await publishHandleRecord(token, attempt.publication);
          },
        });
        localStorage.removeItem(savedUnder);
        writeOwnedHint(signerAddress, handle);
        if (attempt.publication.ownerLookup)
          localStorage.setItem(sharedFlagKey(handle, signerAddress), "1");

        if (currentOwnerKeysRef.current === spendingKeys) {
          lookupVersionRef.current += 1;
          setHandleStatus(`${handle} is ready to receive on ${PAGE_LABEL}.`);
          setClaimedHandle(handle);
          setHandleInput(handle);
          setSuggestedHandle(null);
        }
        addLog(`Handle ready: ${handle}`, "success");
        return true;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        if (currentOwnerKeysRef.current === spendingKeys)
          setHandleStatus(
            e instanceof HandleRegistrationPending
              ? e.message
              : `Registration could not finish: ${msg}`,
          );
        addLog(`Handle finalize failed for ${handle}: ${msg}`, "error");
        // Stop scanning after an interrupted attempt. Keep its name and saved
        // proof available for an explicit retry in Settings.
        return true;
      }
    },
    [
      signer,
      claimedHandle,
      resolveSigner,
      spendingKeys,
      supported,
      registryAddress,
      network,
      HOME_PAGE,
      PAGE_LABEL,
      addLog,
      derivationSignatureB64Ref,
      ensurePoseidon,
      toFieldHex,
      bytes32ToBigInt,
      ensureProxyAuthToken,
      readRegistryState,
      handleIdFor,
      ownerCommitmentFor,
      ensureOwnershipRoot,
    ],
  );

  useEffect(() => {
    if (!spendingKeys) {
      recoveryKeysRef.current = null;
      return;
    }
    if (
      !supported ||
      !proxyAuthReady ||
      !signer ||
      !derivationSignatureB64Ref.current ||
      recoveryKeysRef.current === spendingKeys ||
      registeringRef.current
    )
      return;
    let cancelled = false;
    void (async () => {
      const address = normalizeHandleWallet(await signer.getAddress());
      if (cancelled || currentOwnerKeysRef.current !== spendingKeys) return;
      if (await refreshClaimedHandle()) return;
      // A handle this wallet set up on another network page.
      const hint = readOwnedHint(address);
      if (hint && !cancelled && currentOwnerKeysRef.current === spendingKeys)
        setSuggestedHandle(hint);
      const candidates = [
        ...new Set([
          ...findPendingHandleNames(
            pendingPrefix(registryAddress, address, network),
          ),
          ...(HOME_PAGE
            ? [
                ...findPendingHandleNames(
                  legacyPendingPrefix(registryAddress, address),
                ),
                ...findLocalHandleRegistrations(address),
              ]
            : []),
        ]),
      ].sort();
      if (!candidates.length) return;
      if (
        cancelled ||
        currentOwnerKeysRef.current !== spendingKeys ||
        registeringRef.current
      )
        return;
      recoveryKeysRef.current = spendingKeys;
      registeringRef.current = true;
      setActivity("recover");
      try {
        for (const handle of candidates) {
          if (cancelled || currentOwnerKeysRef.current !== spendingKeys) return;
          if (await finalizeAndPublishHandle(handle, "background")) break;
        }
      } finally {
        registeringRef.current = false;
        setActivity(null);
        setRegistrationRevision((value) => value + 1);
      }
    })().catch(() => {
      // A failed lookup must not invent an assignment or start a registration.
      if (!cancelled && currentOwnerKeysRef.current === spendingKeys)
        setHandleStatus(
          "Your handle could not be checked. Open Settings to retry.",
        );
    });
    return () => {
      cancelled = true;
    };
  }, [
    spendingKeys,
    supported,
    proxyAuthReady,
    signer,
    derivationSignatureB64Ref,
    registryAddress,
    refreshClaimedHandle,
    finalizeAndPublishHandle,
    registrationRevision,
  ]);

  const runExclusive = useCallback(
    async (handle: string, options: { share?: boolean } = {}) => {
      if (registeringRef.current) return;
      registeringRef.current = true;
      setActivity("update");
      try {
        await finalizeAndPublishHandle(handle, "interactive", options);
      } finally {
        registeringRef.current = false;
        setActivity(null);
        setRegistrationRevision((value) => value + 1);
      }
    },
    [finalizeAndPublishHandle],
  );

  /** Registers a new name with no wallet prompt. `link` (an explicit "use my
   * handle here") may ask for the one signature that opens a backup. */
  const registerHandle = useCallback(
    async (requested?: string, options: { link?: boolean } = {}) => {
      if (registeringRef.current || claimedHandle) return;
      const handle = normalizeHandle(
        typeof requested === "string" ? requested : handleInput,
      );
      if (!handle) {
        setHandleStatus("Enter a valid handle, e.g. bob or bob.stealth");
        return;
      }
      registeringRef.current = true;
      setActivity("register");
      setHandleInput(handle);
      try {
        setHandleStatus("Checking your account’s handle...");
        if (await refreshClaimedHandle()) return;
        setHandleStatus("Preparing transaction…");
        await finalizeAndPublishHandle(handle, "interactive", {
          link: options.link === true,
        });
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        console.error("[handle][register] error", e);
        setHandleStatus(`Register failed: ${msg}`);
        addLog(`Handle register failed for ${handle}: ${msg}`, "error");
      } finally {
        registeringRef.current = false;
        setActivity(null);
        setRegistrationRevision((value) => value + 1);
      }
    },
    [
      handleInput,
      claimedHandle,
      addLog,
      finalizeAndPublishHandle,
      refreshClaimedHandle,
    ],
  );

  /** Renews the account's handle (no prompt), or with `share` also seals a
   * backup of its secret for this wallet's other networks (one signature). */
  const updateClaimedHandle = useCallback(
    async (share = false) => {
      if (!claimedHandle) return;
      await runExclusive(claimedHandle, { share });
    },
    [claimedHandle, runExclusive],
  );

  /** Finds the handle this wallet owns, from any network page or browser. */
  const findOwnedHandle = useCallback(
    async (source?: HandleWalletSigner) => {
      if (registeringRef.current || claimedHandle) return;
      registeringRef.current = true;
      setActivity("find");
      try {
        const activeSigner = signer || (await resolveSigner?.());
        if (!activeSigner || !spendingKeys || !supported) {
          setHandleStatus("Connect your wallet first.");
          return;
        }
        setHandleStatus("Confirm the handle signature in your wallet…");
        const wallet = normalizeHandleWallet(await activeSigner.getAddress());
        const root = source
          ? await requestHandleOwnershipRoot(source)
          : await ensureOwnershipRoot(activeSigner, wallet);
        if (currentOwnerKeysRef.current !== spendingKeys) return;
        setHandleStatus("Looking for your handle…");
        const token = await ensureProxyAuthToken({ interactive: false });
        const owned = await fetchOwnedHandles(
          token,
          handleOwnerLookupKey(root),
        );
        if (currentOwnerKeysRef.current !== spendingKeys) return;
        for (const item of owned) {
          if (!item.secret) continue;
          const { handleId } = await handleIdFor(item.handle);
          const state = await readRegistryState(handleId);
          const secret = await unwrapHandleSecret(
            root,
            item.handle,
            item.secret,
          );
          if (
            state.exists &&
            secret &&
            (await ownerCommitmentFor(secret, handleId)) ===
              state.ownerCommitment
          ) {
            if (currentOwnerKeysRef.current !== spendingKeys) return;
            linkedOwnerRef.current = {
              handle: item.handle,
              secret,
              keys: spendingKeys,
            };
            writeOwnedHint(wallet, item.handle);
            setSuggestedHandle(item.handle);
            setHandleInput(item.handle);
            setHandleStatus(
              `${item.handle} is yours. Set it up to receive on ${PAGE_LABEL}.`,
            );
            return;
          }
        }
        if (currentOwnerKeysRef.current !== spendingKeys) return;
        setHandleStatus(
          `No handle is set up for other networks with this wallet. If you registered one elsewhere, open it there and choose "Use on all networks" first.`,
        );
      } catch (e) {
        if (currentOwnerKeysRef.current === spendingKeys)
          setHandleStatus(
            `Could not find your handle: ${e instanceof Error ? e.message : String(e)}`,
          );
      } finally {
        registeringRef.current = false;
        setActivity(null);
        setRegistrationRevision((value) => value + 1);
      }
    },
    [
      claimedHandle,
      signer,
      resolveSigner,
      spendingKeys,
      supported,
      ensureOwnershipRoot,
      ensureProxyAuthToken,
      handleIdFor,
      readRegistryState,
      ownerCommitmentFor,
      PAGE_LABEL,
    ],
  );

  const copyHandle = useCallback(async () => {
    const handle = normalizeHandle(claimedHandle || handleInput);
    if (!handle) {
      setHandleStatus("Enter a valid handle before copying.");
      return;
    }
    await navigator.clipboard.writeText(handle);
    setHandleStatus(`Copied ${handle}`);
  }, [claimedHandle, handleInput]);

  return {
    claimedHandle,
    handleInput,
    setHandleInput,
    handleStatus,
    checkHandleAvailability,
    registerHandle,
    copyHandle,
    refreshClaimedHandle,
    handlesSupported: supported,
    busy: activity !== null,
    registering: activity === "register" || activity === "recover",
    networkLabel: PAGE_LABEL,
    claimedDetails,
    /** This page holds the handle's legacy secret and hasn't backed it up. */
    canShareAcrossNetworks: shareNeeded,
    suggestedHandle,
    findOwnedHandle,
    updateClaimedHandle,
  };
}
